import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { formatRupiah } from '../data/financialData';
import { useBudget } from '../hooks/useBudget';
import { budgetLineKey, BudgetLine } from '../data/budgetData';
import { useData } from '../DataContext';
import { parseGLRows, filterByPeriod, filterByBranch, filterBySingleMonth, matchGLtoBudget, MatchedActual } from '../utils/budgetMatch';

interface BudgetPlanPageProps {
  activeEntity?: string;
  startIdx: number;
  endIdx: number;
}

type TabId = 'plan' | 'variance' | 'summary';

const BudgetPlanPage: React.FC<BudgetPlanPageProps> = ({ activeEntity, startIdx, endIdx }) => {
  const { lines, loading: budgetLoading, error: budgetError, updateLine, resetLine, manualMap, importLines } = useBudget({ entity: activeEntity });
  const { MONTHS } = useData();
  const [activeTab, setActiveTab] = useState<TabId>('plan');
  const [filterNoAccKet, setFilterNoAccKet] = useState<string>('ALL');
  const [filterCabang, setFilterCabang] = useState<string>('ALL');
  const [filterMonth, setFilterMonth] = useState<string>('ALL');
  const [expandedNoAcc, setExpandedNoAcc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const accentColor = activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#10b981';
  const accentGlow = activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(16, 185, 129, 0.15)';

  // GL GAB data — lazy load on variance/summary tab
  const [glData, setGlData] = useState<{ columns: string[]; data: any[][] } | null>(null);
  const [glLoading, setGlLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'plan') return;

    let mounted = true;
    setGlLoading(true);
    setGlData(null);

    const loadGL = async () => {
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

        const periodMonths = MONTHS.slice(startIdx, endIdx + 1).map((m: any) => m.month);
        if (periodMonths.length === 0) {
          setGlLoading(false);
          return;
        }

        const qParams = new URLSearchParams({
          entity: activeEntity ?? 'JUARA_GADAI',
          months: periodMonths.join(',')
        });

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) throw new Error('Gagal memuat GL data dari cloud');
        const data = await res.json();
        if (mounted) {
          setGlData(data);
          setGlLoading(false);
        }
      } catch (err) {
        console.error('Failed to load GL data for budget comparison:', err);
        if (mounted) {
          setGlLoading(false);
        }
      }
    };

    loadGL();
    return () => { mounted = false; };
  }, [activeTab, activeEntity, startIdx, endIdx, MONTHS]);

  // Unique combinations of No Acc - Keterangan for the filter dropdown
  const noAccKetOptions = useMemo(() => {
    const list = lines.map(l => `${l.noAcc} - ${l.keterangan}`);
    return ['ALL', ...Array.from(new Set(list)).sort()];
  }, [lines]);

  const filteredLines = useMemo(() => {
    if (filterNoAccKet === 'ALL') return lines;
    return lines.filter(l => `${l.noAcc} - ${l.keterangan}` === filterNoAccKet);
  }, [lines, filterNoAccKet]);

  // Group by noAcc for Tab 1
  const grouped = useMemo(() => {
    const map = new Map<string, BudgetLine[]>();
    filteredLines.forEach(line => {
      const existing = map.get(line.noAcc) ?? [];
      map.set(line.noAcc, [...existing, line]);
    });
    return map;
  }, [filteredLines]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((noAcc: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(noAcc) ? next.delete(noAcc) : next.add(noAcc);
      return next;
    });
  }, []);

  const handleAmountChange = useCallback((line: BudgetLine, raw: string) => {
    const num = parseFloat(raw.replace(/[^0-9.-]/g, '')) || 0;
    updateLine(line.noAcc, line.keterangan, num, false);
  }, [updateLine]);

  const handleToggleLock = useCallback((line: BudgetLine) => {
    if (!line.isLocked) {
      resetLine(line.noAcc, line.keterangan);
    } else {
      updateLine(line.noAcc, line.keterangan, line.amount, false);
    }
  }, [resetLine, updateLine]);

  // Excel import state
  const [importPreview, setImportPreview] = useState<BudgetLine[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStrategy, setImportStrategy] = useState<'replace' | 'skip'>('replace');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Clear the input value immediately so that selecting the same file a second time triggers onChange!
    e.target.value = '';
    
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json<any>(ws);
    const parsed: BudgetLine[] = json.map((row: any) => {
      const getVal = (possibleKeys: string[]) => {
        for (const k of possibleKeys) {
          if (row[k] !== undefined) return row[k];
          const lowerK = k.toLowerCase().replace(/\s+/g, '');
          for (const key of Object.keys(row)) {
            if (key.toLowerCase().replace(/\s+/g, '') === lowerK) {
              return row[key];
            }
          }
        }
        return undefined;
      };

      const noAccVal = getVal(['No Acc', 'no_acc', 'No acc']);
      const nameAccountVal = getVal(['Name Account', 'name_account']);
      const keteranganVal = getVal(['Keterangan', 'keterangan']);
      const keteranganDetailVal = getVal(['Keterangan detail', 'Keterangan Detail', 'keterangan_detail']);
      const amountVal = getVal(['Amount', 'amount']);
      const userVal = getVal(['User', 'user']);

      const rawAmt = String(amountVal ?? '0').toLowerCase();
      const isKond = rawAmt === 'kondisional';
      const amt = isKond ? 0 : (parseFloat(String(rawAmt).replace(/[^0-9.-]/g, '')) || 0);

      return {
        noAcc: String(noAccVal ?? ''),
        nameAccount: String(nameAccountVal ?? ''),
        keterangan: String(keteranganVal ?? ''),
        keteranganDetail: String(keteranganDetailVal ?? ''),
        defaultAmount: amt,
        isKondisional: isKond,
        amount: amt,
        isLocked: false,
        user: String(userVal ?? ''),
      };
    }).filter((l: BudgetLine) => l.noAcc && l.keterangan);
    setImportPreview(parsed);
    setShowImportModal(true);
  };

  const confirmImport = () => {
    importLines(importPreview, importStrategy);
    setShowImportModal(false);
    setImportPreview([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Auto-detect cabang list from GL data
  const cabangList = useMemo(() => {
    if (!glData) return ['ALL'];
    const set = new Set(parseGLRows(glData.data).map(r => r.branch).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [glData]);

  // Month list — all available months
  const monthList = useMemo(() => {
    return ['ALL', ...MONTHS.map((m: any) => m.month)];
  }, [MONTHS]);

  // GL match results for Tab 2 & 3
  const matchResults = useMemo((): MatchedActual[] => {
    if (!glData) return [];
    const allRows = parseGLRows(glData.data);
    const periodMonths = MONTHS.slice(startIdx, endIdx + 1).map((m: any) => m.month);
    const periodRows = filterByPeriod(allRows, periodMonths);
    const branchRows = filterByBranch(periodRows, filterCabang);
    const monthRows = filterBySingleMonth(branchRows, filterMonth);
    return matchGLtoBudget(filteredLines, monthRows, manualMap);
  }, [glData, filteredLines, manualMap, startIdx, endIdx, MONTHS, filterCabang, filterMonth]);

  const resultByKey = useMemo(() => {
    const map: Record<string, MatchedActual> = {};
    matchResults.forEach(r => { map[r.budgetKey] = r; });
    return map;
  }, [matchResults]);

  // Summary grouped by noAcc for Tab 3
  const noAccSummary = useMemo(() => {
    const map: Record<string, { nameAccount: string; budget: number; actual: number }> = {};
    filteredLines.forEach(line => {
      const key = budgetLineKey(line);
      const actual = resultByKey[key]?.actual ?? 0;
      if (!map[line.noAcc]) {
        map[line.noAcc] = {
          nameAccount: line.nameAccount || '',
          budget: 0,
          actual: 0
        };
      }
      map[line.noAcc].budget += line.amount;
      map[line.noAcc].actual += actual;
    });
    return Object.entries(map).map(([noAcc, vals]) => ({
      noAcc,
      nameAccount: vals.nameAccount,
      budget: vals.budget,
      actual: vals.actual,
      variance: vals.budget - vals.actual,
      pct: vals.budget !== 0 ? ((vals.budget - vals.actual) / Math.abs(vals.budget)) * 100 : 0,
    })).sort((a, b) => b.budget - a.budget);
  }, [filteredLines, resultByKey]);

  // Helper to build detailed list of transaction rows under a specific noAcc
  const getDetailExpList = useCallback((noAccCode: string) => {
    const budgetLinesForNoAcc = filteredLines.filter(l => l.noAcc === noAccCode);
    const rows: Array<{
      noAcc: string;
      branch: string;
      keterangan: string;
      descGL: string;
      budget: number;
      actual: number;
      variance: number;
      pct: number;
      matchStatus: string;
      isBudgetShown: boolean;
    }> = [];
    
    budgetLinesForNoAcc.forEach(line => {
      const key = budgetLineKey(line);
      const res = resultByKey[key];
      
      if (res && res.matchedRows.length > 0) {
        res.matchedRows.forEach((gl, idx) => {
          const variance = line.amount - gl.amount;
          const pct = line.amount !== 0 ? (variance / Math.abs(line.amount)) * 100 : 0;
          rows.push({
            noAcc: line.noAcc,
            branch: gl.branch || '—',
            keterangan: line.keterangan,
            descGL: gl.desc,
            budget: line.amount,
            actual: gl.amount,
            variance: variance,
            pct: pct,
            matchStatus: res.status,
            isBudgetShown: idx === 0,
          });
        });
      } else {
        rows.push({
          noAcc: line.noAcc,
          branch: '—',
          keterangan: line.keterangan,
          descGL: '—',
          budget: line.amount,
          actual: 0,
          variance: line.amount,
          pct: 100,
          matchStatus: 'unmatched',
          isBudgetShown: true,
        });
      }
    });
    
    return rows;
  }, [filteredLines, resultByKey]);

  return (
    <div className="data-table-card animate-in" style={{ padding: '24px', minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
      {(budgetLoading || budgetError) && (
        <div style={{
          padding: '12px 18px',
          marginBottom: '20px',
          borderRadius: 'var(--radius-lg)',
          fontSize: '13px',
          fontWeight: 600,
          backgroundColor: budgetError ? 'rgba(239, 68, 68, 0.08)' : 'rgba(59, 130, 246, 0.08)',
          border: budgetError ? '1.5px solid rgba(239, 68, 68, 0.3)' : '1.5px solid rgba(59, 130, 246, 0.25)',
          color: budgetError ? '#fca5a5' : '#93c5fd',
          display: 'flex', alignItems: 'center', gap: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          {budgetLoading && !budgetError && (
            <>
              <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', marginRight: '4px' }} />
              <span>Memuat budget dari cloud ({activeEntity ?? 'SAHABAT_GADAI'})...</span>
            </>
          )}
          {budgetError && <span>⚠️ {budgetError}</span>}
        </div>
      )}

      {/* 1. Header Panel */}
      <div className="data-table-header" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '16px', marginBottom: '20px' }}>
        <div>
          <div className="data-table-title" style={{ fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋</span> Rencana Anggaran (Budget Plan)
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Profil Aktif: <strong>{activeEntity === 'JUARA_GADAI' ? '🟣 Juara Gadai Group' : '🟢 Sahabat Gadai (JGJB Finance)'}</strong>
          </div>
        </div>
      </div>

      {/* 2. Filters Panel */}
      <div style={{ 
        padding: '16px 20px', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px', 
        borderBottom: '1px solid var(--border-color)', 
        background: 'rgba(255,255,255,0.01)',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flex: 1 }}>
          <div style={{ flex: '0 0 auto', minWidth: '220px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Cari Akun / Keterangan</label>
            <select
              value={filterNoAccKet}
              onChange={e => {
                setFilterNoAccKet(e.target.value);
                setExpandedNoAcc(null);
              }}
              style={{ 
                padding: '8px 12px', 
                fontSize: '13px', 
                border: '1px solid var(--border-light)',
                borderRadius: '6px', 
                backgroundColor: 'var(--bg-input)', 
                color: 'var(--text-primary)', 
                cursor: 'pointer', 
                outline: 'none',
                width: '100%',
                maxWidth: '240px',
                textOverflow: 'ellipsis'
              }}
              title="Filter by No Acc - Keterangan"
            >
              {noAccKetOptions.map(opt => (
                <option key={opt} value={opt}>
                  {opt === 'ALL' ? 'All No Acc - Keterangan' : opt}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '0 0 auto' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Pilih Cabang</label>
            <select
              value={filterCabang}
              onChange={e => setFilterCabang(e.target.value)}
              disabled={cabangList.length <= 1}
              style={{ 
                padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-light)',
                borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', outline: 'none' 
              }}
            >
              {cabangList.map(c => <option key={c} value={c}>{c === 'ALL' ? 'All Cabang' : c}</option>)}
            </select>
          </div>

          <div style={{ flex: '0 0 auto' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Periode Bulan</label>
            <select
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              style={{ 
                padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-light)',
                borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', outline: 'none' 
              }}
            >
              {monthList.map(m => <option key={m} value={m}>{m === 'ALL' ? 'All Bulan' : m}</option>)}
            </select>
          </div>
        </div>

        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ 
              padding: '8px 16px', 
              fontSize: '13px', 
              fontWeight: '700',
              borderRadius: '6px', 
              border: 'none', 
              cursor: 'pointer',
              background: `linear-gradient(135deg, ${accentColor} 0%, rgba(59, 130, 246, 0.8) 100%)`, 
              color: '#fff', 
              boxShadow: `0 4px 12px ${accentGlow}`,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>📥</span> Import Excel Budget
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
      </div>

      {/* 3. Sub-navigation tabs with glassmorphism */}
      <div className="db-tab-container">
        {(['plan', 'variance', 'summary'] as TabId[]).map(tab => (
          <button
            key={tab}
            className={`db-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'plan' ? '📋 Budget Plan' : tab === 'variance' ? '⚖️ Variance Analysis' : '📊 Summary Account'}
          </button>
        ))}
      </div>

      {/* Tab 1: Budget Plan Input */}
      {activeTab === 'plan' && (
        <div style={{ overflowX: 'auto' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          Budget berlaku untuk semua cabang · {filterMonth === 'ALL' ? 'Semua Bulan' : filterMonth}
        </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>No Acc</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Keterangan</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Detail</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>User</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Budget</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>🔒</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(grouped.entries()).map(([noAcc, groupLines]) => {
                const isCollapsed = collapsedGroups.has(noAcc);
                const subtotal = groupLines.reduce((s, l) => s + l.amount, 0);
                const nameAccount = groupLines[0].nameAccount;
                return (
                  <React.Fragment key={noAcc}>
                    <tr
                      onClick={() => toggleGroup(noAcc)}
                      style={{ background: 'var(--sidebar-bg)', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}
                    >
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{noAcc}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }} colSpan={3}>{nameAccount}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{formatRupiah(subtotal)}</td>
                      <td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                        {isCollapsed ? '▶' : '▼'} {groupLines.length} item
                      </td>
                    </tr>
                    {!isCollapsed && groupLines.map(line => (
                      <BudgetRow
                        key={budgetLineKey(line)}
                        line={line}
                        onAmountChange={handleAmountChange}
                        onToggleLock={handleToggleLock}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 2: Variance */}
      {activeTab === 'variance' && (
        <div style={{ overflowX: 'auto' }}>
          {glLoading && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Memuat data GL...</div>}
          {!glLoading && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>No Acc</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Keterangan</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>User</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Budget</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Actual (GL)</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Variance</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>%</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Match</th>
                </tr>
              </thead>
              <tbody>
                {filteredLines.map(line => {
                  const key = budgetLineKey(line);
                  const res = resultByKey[key];
                  const actual = res?.actual ?? 0;
                  const variance = line.amount - actual;
                  const pct = line.amount !== 0 ? (variance / Math.abs(line.amount)) * 100 : 0;
                  const favorable = variance >= 0;
                  const statusIcon = !res || res.status === 'unmatched' ? '❌' : res.status === 'manual' ? '⚠️' : '✅';
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontSize: '12px' }}>{line.noAcc}</td>
                      <td style={{ padding: '6px 12px' }}>{line.keterangan}</td>
                      <td style={{ padding: '6px 12px', fontSize: '12px' }}>{line.user}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatRupiah(line.amount)}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatRupiah(actual)}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', color: favorable ? '#10b981' : '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {favorable ? '-' : '+'}{formatRupiah(Math.abs(variance))}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', color: favorable ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>
                        {pct.toFixed(1)}%
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'center', fontSize: '16px' }} title={res?.status ?? 'unmatched'}>
                        {statusIcon}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tab 3: Summary by No Acc */}
      {activeTab === 'summary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {glLoading && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Memuat data GL...</div>}
          {!glLoading && noAccSummary.map(s => {
            const favorable = s.variance >= 0;
            const barPct = s.budget > 0 ? Math.min((s.actual / s.budget) * 100, 150) : 0;
            const isExpanded = expandedNoAcc === s.noAcc;
            return (
              <div
                key={s.noAcc}
                onClick={() => setExpandedNoAcc(isExpanded ? null : s.noAcc)}
                style={{ 
                  background: 'var(--card-bg)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '10px', 
                  padding: '16px 20px', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isExpanded ? 'var(--shadow-lg)' : 'none',
                  borderColor: isExpanded ? 'var(--text-accent)' : 'var(--border-color)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: 'var(--text-accent)', fontWeight: 700 }}>{s.noAcc}</span>
                    <span style={{ color: 'var(--text-muted)' }}>•</span>
                    <span>{s.nameAccount}</span>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '12px', color: favorable ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {favorable ? 'Under budget' : 'Over budget'} {Math.abs(s.pct).toFixed(1)}%
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', display: 'inline-block' }}>
                      ▼
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '24px', fontSize: '13px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div><span style={{ color: 'var(--text-muted)' }}>Budget: </span><strong>{formatRupiah(s.budget)}</strong></div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Actual: </span><strong>{formatRupiah(s.actual)}</strong></div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Variance: </span><strong style={{ color: favorable ? '#10b981' : '#ef4444' }}>{formatRupiah(s.variance)}</strong></div>
                </div>
                <div style={{ height: '6px', background: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden', marginBottom: isExpanded ? '16px' : '0' }}>
                  <div style={{ height: '100%', width: `${barPct}%`, background: favorable ? '#10b981' : '#ef4444', borderRadius: '3px', transition: 'width 0.4s' }} />
                </div>

                {/* Expanded Detail Table */}
                {isExpanded && (
                  <div 
                    onClick={e => e.stopPropagation()} // Prevent collapse on table click
                    style={{ 
                      marginTop: '16px', 
                      paddingTop: '16px', 
                      borderTop: '1px dashed var(--border-color)', 
                      overflowX: 'auto',
                      cursor: 'default'
                    }}
                  >
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: 'var(--text-accent)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🔍</span> Detail Pengeluaran Akun {s.noAcc} — {s.nameAccount}
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                          <th style={{ padding: '8px 6px' }}>No ACC</th>
                          <th style={{ padding: '8px 6px' }}>kode Cabang</th>
                          <th style={{ padding: '8px 6px' }}>Keterangan</th>
                          <th style={{ padding: '8px 6px' }}>Deskripsi pada GL GAB</th>
                          <th style={{ padding: '8px 6px', textAlign: 'right' }}>Budget</th>
                          <th style={{ padding: '8px 6px', textAlign: 'right' }}>Actual</th>
                          <th style={{ padding: '8px 6px', textAlign: 'right' }}>Variance</th>
                          <th style={{ padding: '8px 6px', textAlign: 'right' }}>%</th>
                          <th style={{ padding: '8px 6px', textAlign: 'center' }}>Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const details = getDetailExpList(s.noAcc);
                          if (details.length === 0) {
                            return (
                              <tr>
                                <td colSpan={9} style={{ padding: '12px 6px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                  Tidak ada detail transaksi pengeluaran.
                                </td>
                              </tr>
                            );
                          }
                          return details.map((d, idx) => (
                            <DetailRow key={idx} d={d} idx={idx} />
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          preview={importPreview}
          strategy={importStrategy}
          onStrategyChange={setImportStrategy}
          onConfirm={confirmImport}
          onCancel={() => { setShowImportModal(false); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        />
      )}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .db-tab-container {
          display: flex;
          gap: 6px;
          background: rgba(255, 255, 255, 0.015);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 30px;
          padding: 4px;
          width: fit-content;
          margin: 0 auto 24px 0;
          backdrop-filter: blur(10px);
          box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .db-tab {
          padding: 8px 18px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          border: 1px solid transparent;
          background: none;
          outline: none;
          color: var(--text-secondary);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 6px;
          border-radius: 25px;
          font-family: 'Inter', sans-serif;
        }
        .db-tab:hover {
          color: var(--text-primary);
          background-color: rgba(255, 255, 255, 0.03);
          transform: translateY(-1px);
        }
        .db-tab.active {
          color: ${activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '#a7f3d0'};
          background: ${activeEntity === 'JUARA_GADAI' 
            ? 'linear-gradient(135deg, rgba(246, 139, 31, 0.15) 0%, rgba(48, 55, 149, 0.25) 100%)' 
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(4, 120, 87, 0.25) 100%)'};
          border-color: ${activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.25)' : 'rgba(16, 185, 129, 0.25)'};
          box-shadow: 0 4px 12px ${activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.12)' : 'rgba(16, 185, 129, 0.12)'};
          transform: translateY(0);
        }
      `}} />
    </div>
  );
};

// Detail transaction row with inline hover states
const DetailRow: React.FC<{
  d: {
    noAcc: string;
    branch: string;
    keterangan: string;
    descGL: string;
    budget: number;
    actual: number;
    variance: number;
    pct: number;
    matchStatus: string;
    isBudgetShown: boolean;
  };
  idx: number;
}> = ({ d, idx }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isFav = d.variance >= 0;
  const statusIcon = d.matchStatus === 'unmatched' ? '❌' : d.matchStatus === 'manual' ? '⚠️' : '✅';
  const matchTitle = d.matchStatus === 'unmatched' ? 'Belum dicocokkan' : d.matchStatus === 'manual' ? 'Pencocokan Manual' : 'Pencocokan Otomatis';
  return (
    <tr 
      style={{ 
        borderBottom: '1px solid var(--border-color)', 
        transition: 'background 0.2s',
        background: isHovered ? 'rgba(255, 255, 255, 0.03)' : 'transparent'
      }} 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <td style={{ padding: '8px 6px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{d.noAcc}</td>
      <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontWeight: d.branch !== '—' ? 600 : 400 }}>{d.branch}</td>
      <td style={{ padding: '8px 6px' }}>{d.keterangan}</td>
      <td style={{ padding: '8px 6px', color: d.descGL !== '—' ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: d.descGL === '—' ? 'italic' : 'normal', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.descGL}>
        {d.descGL}
      </td>
      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: d.isBudgetShown ? 500 : 400, color: d.isBudgetShown ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
        {d.isBudgetShown ? formatRupiah(d.budget) : '—'}
      </td>
      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatRupiah(d.actual)}</td>
      <td style={{ padding: '8px 6px', textAlign: 'right', color: isFav ? '#10b981' : '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {isFav ? '-' : '+'}{formatRupiah(Math.abs(d.variance))}
      </td>
      <td style={{ padding: '8px 6px', textAlign: 'right', color: isFav ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>
        {d.pct.toFixed(1)}%
      </td>
      <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '15px' }} title={matchTitle}>
        {statusIcon}
      </td>
    </tr>
  );
};

// Single budget row with inline edit
const BudgetRow: React.FC<{
  line: BudgetLine;
  onAmountChange: (line: BudgetLine, raw: string) => void;
  onToggleLock: (line: BudgetLine) => void;
}> = ({ line, onAmountChange, onToggleLock }) => {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');

  const startEdit = () => {
    if (line.isLocked) return;
    setInputVal(String(line.amount));
    setEditing(true);
  };

  const commitEdit = () => {
    onAmountChange(line, inputVal);
    setEditing(false);
  };

  return (
    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
      <td style={{ padding: '6px 12px 6px 24px', color: 'var(--text-muted)', fontSize: '12px' }}>{line.noAcc}</td>
      <td style={{ padding: '6px 12px' }}>{line.keterangan}</td>
      <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={line.keteranganDetail}>{line.keteranganDetail}</td>
      <td style={{ padding: '6px 12px', fontSize: '12px' }}>{line.user}</td>
      <td style={{ padding: '6px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        {editing ? (
          <input
            autoFocus
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(false); }}
            style={{ width: '100px', textAlign: 'right', padding: '2px 6px', border: '1px solid var(--text-accent)', borderRadius: '4px', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '13px' }}
          />
        ) : (
          <span
            onClick={startEdit}
            style={{ cursor: line.isLocked ? 'default' : 'pointer', padding: '2px 6px', borderRadius: '4px', background: !line.isLocked ? 'var(--sidebar-bg)' : 'transparent' }}
          >
            {formatRupiah(line.amount)}
          </span>
        )}
      </td>
      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
        {line.isKondisional && line.isLocked && (
          <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: 'rgba(251,191,36,0.15)', color: '#f59e0b' }}>Kondisional</span>
        )}
        {!line.isLocked && (
          <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>Custom</span>
        )}
      </td>
      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
        <button
          onClick={() => onToggleLock(line)}
          title={line.isLocked ? 'Unlock untuk edit' : 'Reset ke default'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', opacity: 0.7 }}
        >
          {line.isLocked ? '🔒' : '🔓'}
        </button>
      </td>
    </tr>
  );
};

// Premium Import Preview Modal
const ImportModal: React.FC<{
  preview: BudgetLine[];
  strategy: 'replace' | 'skip';
  onStrategyChange: (s: 'replace' | 'skip') => void;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ preview, strategy, onStrategyChange, onConfirm, onCancel }) => {
  return (
    <div 
      style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(15, 23, 42, 0.75)', 
        backdropFilter: 'blur(8px)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1100, 
        animation: 'fadeIn 0.3s ease'
      }}
    >
      <div 
        style={{ 
          background: 'var(--card-bg)', 
          border: '1px solid var(--border-color)',
          borderRadius: '16px', 
          padding: '28px', 
          width: '800px', 
          maxWidth: '92vw',
          maxHeight: '82vh', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color)', paddingBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
            <span>📥</span> Import Preview 
            <span style={{ fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '12px', background: 'rgba(59,130,246,0.12)', color: 'var(--text-accent)' }}>
              {preview.length} baris terdeteksi
            </span>
          </h3>
          <button 
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer', opacity: 0.7 }}
          >
            ✕
          </button>
        </div>

        {/* Strategy Selector - Premium Segmented Control */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Metode Penggabungan (Merge Strategy):
          </label>
          <div style={{ display: 'flex', background: 'var(--sidebar-bg)', padding: '4px', borderRadius: '8px', gap: '4px', width: 'fit-content' }}>
            <button
              type="button"
              onClick={() => onStrategyChange('replace')}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                background: strategy === 'replace' ? 'var(--text-accent)' : 'transparent',
                color: strategy === 'replace' ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.2s ease'
              }}
            >
              🔄 Ganti yang Ada (Replace Existing)
            </button>
            <button
              type="button"
              onClick={() => onStrategyChange('skip')}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                background: strategy === 'skip' ? 'var(--text-accent)' : 'transparent',
                color: strategy === 'skip' ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.2s ease'
              }}
            >
              ⏭ Lewati yang Ada (Skip Existing)
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--border-color)', borderRadius: '10px', background: 'rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--sidebar-bg)', padding: '12px 14px', textAlign: 'left', width: '15%', color: 'var(--text-primary)', fontWeight: 700 }}>No Acc</th>
                <th style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--sidebar-bg)', padding: '12px 14px', textAlign: 'left', width: '45%', color: 'var(--text-primary)', fontWeight: 700 }}>Keterangan</th>
                <th style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--sidebar-bg)', padding: '12px 14px', textAlign: 'right', width: '20%', color: 'var(--text-primary)', fontWeight: 700 }}>Amount</th>
                <th style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--sidebar-bg)', padding: '12px 14px', textAlign: 'left', width: '20%', color: 'var(--text-primary)', fontWeight: 700 }}>User</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-color)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{l.noAcc}</td>
                  <td style={{ padding: '10px 14px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }} title={l.keterangan}>
                    {l.keterangan}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--text-accent)' }}>
                    {l.isKondisional ? (
                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', background: 'rgba(251,191,36,0.15)', color: '#f59e0b' }}>Kondisional</span>
                    ) : (
                      l.amount.toLocaleString('id-ID')
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{l.user || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
          <button 
            onClick={onCancel} 
            style={{ 
              padding: '8px 20px', 
              borderRadius: '8px', 
              border: '1px solid var(--border-color)', 
              background: 'transparent', 
              cursor: 'pointer', 
              color: 'var(--text-primary)',
              fontWeight: 500,
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            Batal
          </button>
          <button 
            onClick={onConfirm} 
            style={{ 
              padding: '8px 24px', 
              borderRadius: '8px', 
              border: 'none', 
              background: 'linear-gradient(135deg, var(--text-accent) 0%, #3b82f6 100%)', 
              color: '#fff', 
              cursor: 'pointer', 
              fontWeight: 600,
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
              transition: 'all 0.2s'
            }}
          >
            Terapkan Import
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetPlanPage;
